<script setup lang="ts">
/**
 * MY — Resignation of Director (M32106) 문서-뷰 패널.
 * Studio 셸 + ConversationPanel 챗봇 연동 (CDD 패턴).
 * UI: package PresetMVP DirectorResignationDoc 이식 (정관번호 생략).
 */
import { ref, reactive, computed, provide, watch, onMounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail } from '@/features/AI-documents/types/docGen.types';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import { CDD_DOC_CTX } from './cdd/cddContext';
import { labelize, typeOf, progressKeysForRows, DOC_SHEET_PATTERNS } from './resignation/resignFields';
import { buildResignRegisterDirectors } from './resignation/buildResignRegisterDirectors';
import DirectorResignationDoc from './resignation/DirectorResignationDoc.vue';
import { buildResignFormData } from '../mappers/directorResignationMapper';
import { parseMalaysianAddress, parseMyAddressObject, isEmptyMyAddress } from '@/shared/utils/malaysianAddress';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import { prefetchB15Cache } from '@/features/startTask/preset_task/shared/preset/lookups/b15NationalityCache';
import { fetchEntityAddresses } from '@/shared/composables/useEntityAddresses';
import AddressInputModal from './cdd/AddressInputModal.vue';
import { comboSourceOf } from './cdd/cddFields';
import { comboOptions } from './cdd/cddOptions';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  activePersonContext?: Record<string, any> | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  activeAddressContext?: { fieldGroup: string; index: number } | null;
  editTaskId?: number | null;
  studio?: boolean;
  pendingDocType?: string;
  appliedAddress?: { key: string; line: string; town: string; state: string; postcode: string; country: string; seq: number } | null;
  /** 챗봇 formData (정관번호 등 Studio 채팅 결과 반영) */
  chatFormData?: Record<string, any> | null;
  /** 챗봇 정관번호 위젯 Continue — 번호 + Governing Document 라디오 동시 반영 */
  appliedClause?: {
    clauseNumber: string;
    governingBasis: 'constitution' | 'articles' | 'third_schedule' | null;
    rawOption?: string;
    seq: number;
  } | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', ctx: Record<string, any>): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'progress-change', pct: number): void;
  (e: 'request-address-input', key: string): void;
}>();

const router = useRouter();
const docRef = ref<InstanceType<typeof DirectorResignationDoc> | null>(null);
const values = reactive<Record<string, string>>({});
const activeKey = ref('');
const activeCheckbox = ref('');
const inputKey = ref('');
const inputText = ref('');
const dateMenu = ref(false);

const entityCd = computed(() =>
  String(props.entityData?.entityCd || props.entityData?.icpCd || '').trim(),
);

const taskTitleLocal = ref('');
const titleModel = computed({
  get: () => taskTitleLocal.value || props.taskTitle || '',
  set: (v: string) => { taskTitleLocal.value = v; },
});
const effectiveTitle = computed(() => titleModel.value.trim());

function setFieldValue(key: string, val: string) {
  values[key] = val;
  // 자동 싸이너는 사임이사 Full Name 선택/입력 시에만
  if (/^dir\d+_full_name$/.test(key) && String(val || '').trim()) {
    scheduleAutoSigners();
  }
}
function activate(key: string) {
  activeKey.value = key;
  const t = typeOf(key);
  if (key === 'constitution_clause_number' || t === 'clause') {
    const local = (docRef.value as any)?.gatherLocalState?.() || {};
    emit('request-person-lookup', {
      fieldGroup: 'constitution-clause',
      key: 'constitution_clause_number',
      clauseOptions: articleNoOptions.value,
      defaultValue: values.constitution_clause_number || '',
      defaultBasis: local.basis || 'constitution',
    });
    inputKey.value = '';
    return;
  }
  if (t === 'person' || t === 'entity') {
    emit('request-person-lookup', {
      fieldGroup: 'resignation',
      key,
      defaultRole: t === 'person' ? 'DIRECTOR' : null,
      lookupType: key === 'company_name' || t === 'entity' ? 'entity' : 'officer',
    });
    inputKey.value = '';
  } else {
    inputKey.value = key;
    inputText.value = values[key] || '';
    if (t === 'date') dateMenu.value = true;
  }
}
function onPickDate(d: any) {
  const date = Array.isArray(d) ? d[0] : d;
  if (!date) return;
  const dt = new Date(date);
  inputText.value = Number.isNaN(dt.getTime())
    ? String(date)
    : `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
  dateMenu.value = false;
  commitInput();
}
function commitInput() {
  if (inputKey.value) values[inputKey.value] = inputText.value.trim();
  inputKey.value = '';
  activeKey.value = '';
}
function skipInput() {
  inputKey.value = '';
  activeKey.value = '';
}

const addrModal = ref(false);
const addrKey = ref('');
const addrLabel = ref('');
const addrInitial = ref<{ line?: string; town?: string; state?: string; postcode?: string; country?: string }>({});

function openAddress(key: string) {
  if (props.studio) {
    addrKey.value = key;
    emit('request-address-input', key);
    return;
  }
  addrKey.value = key;
  addrLabel.value = labelize(key);
  addrInitial.value = { line: values[key] || '' };
  addrModal.value = true;
}
function applyAddressTo(key: string, a: { line: string; town: string; state: string; postcode: string; country: string }) {
  if (!key) return;
  values[key] = [a.line, [a.postcode, a.town].filter(Boolean).join(' '), a.state, a.country]
    .map((s) => String(s || '').trim())
    .filter(Boolean)
    .join(', ')
    .toUpperCase();
}
function applyAddress(a: { line: string; town: string; state: string; postcode: string; country: string }) {
  applyAddressTo(addrKey.value, a);
}
watch(() => props.appliedAddress, (a) => { if (a?.key) applyAddressTo(a.key, a); });

/** 챗봇에서 정관번호 선택 시 — 번호 + Governing Document 라디오 동시 반영 */
function applyClausePick(pick: {
  clauseNumber?: string | null;
  governingBasis?: string | null;
  rawOption?: string | null;
} | null | undefined) {
  if (!pick) return;
  let basis = pick.governingBasis || null;
  if (!basis && pick.rawOption) basis = detectGoverningBasis(String(pick.rawOption));
  if (basis === 'constitution' || basis === 'articles' || basis === 'third_schedule') {
    (docRef.value as any)?.setBasis?.(basis);
  }
  if (basis === 'third_schedule') {
    values.constitution_clause_number = '';
    return;
  }
  const num = parseClauseNo(String(pick.clauseNumber || pick.rawOption || ''));
  if (num) values.constitution_clause_number = num;
}

async function applyClausePickWhenReady(pick: {
  clauseNumber?: string | null;
  governingBasis?: string | null;
  rawOption?: string | null;
} | null | undefined) {
  if (!pick) return;
  await nextTick();
  applyClausePick(pick);
  // 문서 마운트 전이면 한 번 더
  if (!docRef.value) {
    await new Promise((r) => setTimeout(r, 80));
    applyClausePick(pick);
  }
}

watch(
  () => props.appliedClause?.seq,
  () => {
    if (props.appliedClause) void applyClausePickWhenReady(props.appliedClause);
  },
);
watch(
  () => props.chatFormData?._clauseSyncAt,
  () => { void applyClausePickWhenReady(props.chatFormData ? {
    clauseNumber: props.chatFormData.constitution_clause_number,
    governingBasis: props.chatFormData._resignGoverningBasis,
    rawOption: props.chatFormData._clauseRawOption,
  } : null); },
);

function comboOptionsOf(key: string) {
  return comboOptions(comboSourceOf(key));
}
const pendingPickValue = ref('');
const stubRowCount = ref(0);

provide(CDD_DOC_CTX, {
  valueOf: (k) => values[k] || '',
  labelOf: labelize,
  typeOf,
  activeKey,
  activate,
  setFieldValue,
  openAddress,
  checkboxValue: activeCheckbox,
  setCheckbox: () => {},
  comboOptionsOf,
  shareholderRowCount: stubRowCount,
  boRowCount: stubRowCount,
  addShareholderRow: () => {},
  removeShareholderRow: () => {},
  addBoRow: () => {},
  removeBoRow: () => {},
  pendingPickValue,
  placePick: () => false,
});

function applyPerson(key: string, a: any) {
  const name =
    a.name ||
    a.contactName ||
    [a.firstName, a.middleName, a.surname].filter(Boolean).join(' ') ||
    '';
  values[key] = name;

  if (typeOf(key) === 'entity' || key === 'company_name') {
    values.company_name = name || a.entityName || a.companyName || values.company_name || '';
    values.company_registration_no =
      a.registrationNo || a.contactRegNo || a.regNo || values.company_registration_no || '';
    const fullAddr = a.registeredAddress || a.primaryAddress || a.residentialAddress || '';
    const res =
      a.myResidential && !isEmptyMyAddress(a.myResidential)
        ? a.myResidential
        : parseMalaysianAddress(fullAddr);
    if (!isEmptyMyAddress(res)) {
      values.company_address = [res.street || fullAddr, [res.postcode, res.town].filter(Boolean).join(' '), res.state, res.country]
        .filter(Boolean)
        .join(', ');
    } else if (fullAddr) {
      values.company_address = fullAddr;
    }
    // entity pick — 이사 수 Sole/Multiple 동기화
    const cd = String(a.entityCd || a.icpCd || entityCd.value || '').trim();
    if (cd) (docRef.value as any)?.syncDirectorModeFromEntity?.(cd);
    return;
  }

  const m = key.match(/^dir(\d+)_full_name$/);
  if (!m) return;
  const id = Number(m[1]);
  // NRIC / Passport 자동 체크 — nationalityCd(B15135) / idType / reg 형태
  const idKind = resolvePersonIdKind(a);
  (docRef.value as any)?.setNationality?.(id, idKind);

  const idNo =
    idKind === 'malaysian'
      ? (a.nricNo || a.contactRegNo || a.passportNo || '')
      : (a.passportNo || a.contactRegNo || a.nricNo || '');
  if (idNo) values[`dir${id}_ic_passport_no`] = idNo;

  const fullAddr = a.residentialAddress || a.primaryAddress || '';
  const res =
    a.myResidential && !isEmptyMyAddress(a.myResidential)
      ? a.myResidential
      : parseMalaysianAddress(fullAddr);
  if (!isEmptyMyAddress(res)) {
    values[`dir${id}_residential_address`] = [res.street || fullAddr, [res.postcode, res.town].filter(Boolean).join(' '), res.state, res.country]
      .filter(Boolean)
      .join(', ');
  } else if (fullAddr) {
    values[`dir${id}_residential_address`] = fullAddr;
  }

  // Register(L_OFFICER.CESSATION_DATE) 용 — contactCd / officerIdx / 임명일 persist
  let contactCd = String(a.contactCd || a.CONTACT_CD || '').trim();
  let officerIdx = a.officerIdx ?? a.OFFICER_IDX ?? '';
  let appointmentDate = String(a.appointmentDate || a.APPOINTMENT_DATE || '').trim();
  if ((!contactCd || !officerIdx) && entityDirectors.value.length) {
    const matched = entityDirectors.value.find((d) => {
      const dCd = String(d.contactCd || '').trim();
      if (contactCd && dCd && dCd === contactCd) return true;
      return normName(d.name || d.contactName || '') === normName(name);
    });
    if (matched) {
      if (!contactCd) contactCd = String(matched.contactCd || '').trim();
      if (!officerIdx) officerIdx = matched.officerIdx ?? '';
      if (!appointmentDate) {
        appointmentDate = String(matched.appointmentDate || matched.APPOINTMENT_DATE || '').trim();
      }
    }
  }
  if (contactCd) values[`dir${id}_contact_cd`] = contactCd;
  if (officerIdx != null && String(officerIdx).trim()) {
    values[`dir${id}_officer_idx`] = String(officerIdx).trim();
  }
  if (appointmentDate) values[`dir${id}_appointment_date`] = appointmentDate;

  scheduleAutoSigners();
}

/** B15135=Malaysia. NRIC 12자리 / passport / idType 으로 판별 */
function resolvePersonIdKind(a: any): 'malaysian' | 'foreigner' {
  const natCd = String(a.nationalityCd || a.nationalityCode || a.NATIONALITY_CD || '').trim().toUpperCase();
  const natName = String(a.nationality || a.nationalityName || a.NATIONALITY_NAME || '').trim();
  if (natCd === 'B15135' || /^malaysia$/i.test(natName)) return 'malaysian';
  if (natCd || (natName && !/malaysia/i.test(natName))) return 'foreigner';

  const idType = String(a.idTypeCd || a.ID_TYPE_CD || a.idTypeName || a.idType || '').trim();
  if (/pass\s*port|ppt|foreign/i.test(idType)) return 'foreigner';
  if (/nric|mykad|my\s*kad|ic\b/i.test(idType)) return 'malaysian';

  if (a.passportNo && !a.nricNo) return 'foreigner';
  if (a.nricNo && !a.passportNo) return 'malaysian';

  const reg = String(a.nricNo || a.passportNo || a.contactRegNo || a.CONTACT_REG_NO || '').replace(/[\s-]/g, '');
  const digits = reg.replace(/\D/g, '');
  if (digits.length === 12) return 'malaysian';
  if (reg && digits.length !== 12) return 'foreigner';
  return 'malaysian';
}

function parseClauseNo(input: string): string {
  const s = String(input || '').trim();
  if (!s) return '';
  const m1 = s.match(/(?:Article|Clause)\s+([\w()]+)/i);
  if (m1) return m1[1];
  if (!/article|clause|constitution|association|schedule/i.test(s)) return s;
  const m2 = s.match(/\b(\d+[\w()]*)/);
  return m2 ? m2[1] : s;
}

function detectGoverningBasis(opt: string): 'constitution' | 'articles' | 'third_schedule' | null {
  const s = String(opt || '');
  if (/third\s*schedule/i.test(s)) return 'third_schedule';
  if (/\barticles?\b/i.test(s) || /\bregulation\b/i.test(s)) return 'articles';
  if (/\bconstitution\b/i.test(s) || /\bclause\b/i.test(s)) return 'constitution';
  return null;
}

function normName(s: string): string {
  return String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();
}

function toSignerEntry(o: any, role: string, title: string, idSuffix = ''): any {
  const name = String(o?.name || o?.contactName || '').trim();
  const baseId = String(o?.id || o?.contactCd || o?.officerIdx || `${role}-${name}`);
  return {
    id: idSuffix ? `${baseId}-${idSuffix}` : baseId,
    name,
    title,
    role,
    email: String(o?.email || o?.contactEmail || '').trim(),
    contactCd: o?.contactCd,
    officerIdx: o?.officerIdx,
    showTitle: true,
    _signerLayout: 'box',
  };
}

const articleNoOptions = ref<string[]>([]);
const entityDirectors = ref<any[]>([]);
const entitySecretaries = ref<any[]>([]);
let autoSignerTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleAutoSigners() {
  if (autoSignerTimer) clearTimeout(autoSignerTimer);
  autoSignerTimer = setTimeout(() => { void runAutoSigners(); }, 120);
}

async function loadEntityOfficersAndOptions(cd: string) {
  if (!cd) return;
  try {
    const [dirsJson, secsJson, optsJson] = await Promise.all([
      fetch(`/api/documents/variables/directors/${encodeURIComponent(cd)}`, { credentials: 'include' })
        .then((r) => r.json()).catch(() => null),
      fetch(`/api/documents/variables/secretaries/${encodeURIComponent(cd)}`, { credentials: 'include' })
        .then((r) => r.json()).catch(() => null),
      fetch(`/api/entity_my/profile/resolution-options`, { credentials: 'include' })
        .then((r) => r.json()).catch(() => null),
    ]);
    const dirs = dirsJson?.data?.directors || dirsJson?.directors || [];
    const secs = secsJson?.data?.secretaries || secsJson?.secretaries || [];
    entityDirectors.value = Array.isArray(dirs)
      ? dirs.filter((d: any) => !String(d.cessationDate || d.CESSATION_DATE || '').trim())
      : [];
    entitySecretaries.value = Array.isArray(secs)
      ? secs.filter((s: any) => !String(s.cessationDate || s.CESSATION_DATE || '').trim())
      : [];
    const opts =
      optsJson?.articleNoOptions ||
      optsJson?.data?.resolution?.articleNoOptions ||
      optsJson?.resolution?.articleNoOptions ||
      [];
    articleNoOptions.value = Array.isArray(opts)
      ? Array.from(new Set(opts.map((o: any) => String(o ?? '').trim()).filter(Boolean)))
      : [];
    // 챗봇 퀵메뉴 clause-number 위젯이 쓸 수 있도록 formData에 옵션 캐시(있으면)
    // options 만 캐시 — 자동 싸이너는 사임이사 선택 시에만
  } catch (err) {
    console.warn('[Resignation] officers/options load failed:', err);
  }
}

function runAutoSigners(mode: 'full' | 'fill-empty' = 'full') {
  const local = (docRef.value as any)?.gatherLocalState?.() || { rows: [1] };
  const rowIds: number[] = Array.isArray(local.rows) && local.rows.length ? local.rows : [1];
  const resigningNorms = new Set(
    rowIds.map((id) => normName(values[`dir${id}_full_name`] || '')).filter(Boolean),
  );
  // 해임(사임) 이사 Full Name 이 잡힌 뒤에만 자동 싸이너 — 문서/엔티티 진입 직후엔 비움
  if (!resigningNorms.size) return;

  const boardComputed = entityDirectors.value
    .filter((d) => {
      const n = normName(d.name || d.contactName || '');
      return n && !resigningNorms.has(n);
    })
    .map((d) => toSignerEntry(d, 'director', d.positionName || 'Director', 'dcr'));

  const secretariesComputed = entitySecretaries.value.map((s) =>
    toSignerEntry(s, 'secretary', s.positionName || 'Company Secretary', 'sec'),
  );

  const letterByRow: Record<number, any[]> = {};
  const secretariesByRow: Record<number, any[]> = {};
  for (const id of rowIds) {
    const name = String(values[`dir${id}_full_name`] || '').trim();
    if (!name) letterByRow[id] = [];
    else {
      const matched = entityDirectors.value.find(
        (d) => normName(d.name || d.contactName || '') === normName(name),
      );
      // Person pick 없이 이름만 채운 경우에도 Register 용 officer 식별자 보강
      if (matched) {
        if (!String(values[`dir${id}_contact_cd`] || '').trim() && matched.contactCd) {
          values[`dir${id}_contact_cd`] = String(matched.contactCd).trim();
        }
        if (!String(values[`dir${id}_officer_idx`] || '').trim() && matched.officerIdx != null) {
          values[`dir${id}_officer_idx`] = String(matched.officerIdx).trim();
        }
        if (!String(values[`dir${id}_appointment_date`] || '').trim()) {
          const ap = String(matched.appointmentDate || matched.APPOINTMENT_DATE || '').trim();
          if (ap) values[`dir${id}_appointment_date`] = ap;
        }
      }
      letterByRow[id] = [
        matched
          ? toSignerEntry(matched, 'director', 'Director', `ltr-${id}`)
          : toSignerEntry({ id: `resign-${id}`, name }, 'director', 'Director', `ltr-${id}`),
      ];
    }

    const existing58 = parseSignersArr(local.signers58?.[id] || local.signers58?.[String(id)]);
    secretariesByRow[id] =
      mode === 'fill-empty' && existing58.length
        ? existing58
        : secretariesComputed.map((s) => ({ ...s, id: `${s.id}-58-${id}` }));
  }

  const keepBoard = mode === 'fill-empty' && Array.isArray(local.signers) && local.signers.length > 0;

  (docRef.value as any)?.applyAutoSigners?.({
    board: keepBoard ? local.signers.map((s: any) => ({ ...s })) : boardComputed,
    secretariesByRow,
    letterByRow,
  });
}

watch(
  () => props.selectedPerson,
  (p) => {
    const ctx = props.activePersonContext;
    if (!p || !ctx || ctx.fieldGroup !== 'resignation') return;
    applyPerson(String(ctx.key || ''), p as any);
  },
);

function formatCompanyAddress(raw: any): string {
  if (!raw) return '';
  if (typeof raw === 'string') {
    const parsed = parseMalaysianAddress(raw);
    if (!isEmptyMyAddress(parsed)) {
      return [parsed.street, [parsed.postcode, parsed.town].filter(Boolean).join(' '), parsed.state, parsed.country]
        .filter(Boolean)
        .join(', ');
    }
    return raw.trim();
  }
  const parsed = parseMyAddressObject(raw);
  if (!isEmptyMyAddress(parsed)) {
    return [parsed.street, [parsed.postcode, parsed.town].filter(Boolean).join(' '), parsed.state, parsed.country]
      .filter(Boolean)
      .join(', ');
  }
  const lines = [raw.addressLine1, raw.addressLine2, raw.addressLine3, raw.street01, raw.street02]
    .map((x: any) => String(x || '').trim())
    .filter(Boolean);
  const city = String(raw.city || raw.town || '').trim();
  const state = String(raw.state || '').trim();
  const post = String(raw.postcode || raw.post_code || raw.postalCode || '').trim();
  const country = String(raw.country || '').trim();
  return [lines.join(', '), [post, city].filter(Boolean).join(' '), state, country].filter(Boolean).join(', ');
}

const lastSyncedEntityCd = ref('');

function applyCompanyFromEntity(e: Record<string, any>, force = false) {
  const name = e.companyName || e.entityName || e.name || '';
  if (name && (force || !values.company_name)) values.company_name = name;
  const reg = e.registrationNo || e.regNo || e.REG_NO || e.registrationNumber || '';
  const regOld = e.regNoOld || e.REG_NO_OLD || e.registrationNoOld || '';
  if (reg && (force || !values.company_registration_no)) {
    values.company_registration_no =
      regOld && String(regOld) !== String(reg) ? `${reg} (${regOld})` : String(reg);
  }
  const addrSrc =
    e.registeredAddress ||
    e.registered_address ||
    e.companyAddress ||
    e.address ||
    e.REGISTERED_ADDRESS ||
    null;
  const formatted = formatCompanyAddress(addrSrc);
  if (formatted && (force || !values.company_address)) values.company_address = formatted;
}

/** 등록주소·회사정보 + 정관(라디오 + 번호만) 자동 채움 */
async function loadEntityCompanyProfile(cd: string, force = false) {
  if (!cd) return;
  try {
    const [entityAddr, addrJson] = await Promise.all([
      fetchEntityAddresses(cd).catch(() => null),
      fetch(`/api/setupEntity/changeAddress/address-info/${encodeURIComponent(cd)}`, {
        credentials: 'include',
      })
        .then((r) => r.json())
        .catch(() => null),
    ]);

    if (entityAddr) {
      if (entityAddr.companyName && (force || !values.company_name)) {
        values.company_name = entityAddr.companyName;
      }
      if (entityAddr.regNo && (force || !values.company_registration_no)) {
        values.company_registration_no = entityAddr.regNo;
      }
      const registered =
        entityAddr.registered ||
        entityAddr.registeredOfficeAddress?.composed ||
        '';
      if (registered && (force || !values.company_address)) values.company_address = registered;
    }

    // 정관: 라디오 + 숫자만 (예: "Clause 36 of …" → Constitution + "36")
    const regClause = String(
      addrJson?.clauseNo ||
        addrJson?.articleNo ||
        entityAddr?.clauseNo ||
        entityAddr?.articleNo ||
        '',
    ).trim();
    if (regClause) {
      applyRegisteredClauseToDoc(regClause);
    }
  } catch (err) {
    console.warn('[Resignation] entity address load failed:', err);
  }
}

/** entity 등록 정관 텍스트 → basis 라디오 + clause 번호만 */
function applyRegisteredClauseToDoc(regClause: string) {
  const s = String(regClause || '').trim();
  if (!s) return;
  const basis = detectGoverningBasis(s) || 'constitution';
  (docRef.value as any)?.setBasis?.(basis);
  if (basis === 'third_schedule') {
    values.constitution_clause_number = '';
  } else {
    const num = parseClauseNo(s);
    if (num) values.constitution_clause_number = num;
  }
}

// Entity 선택 시 회사 필드 자동 채움 + 이사/비서/정관 옵션 + Sole/Multiple
watch(
  () => props.entityData,
  (e) => {
    if (!e) {
      lastSyncedEntityCd.value = '';
      return;
    }
    const cd = String(e.entityCd || e.icpCd || entityCd.value || '').trim();
    const force = !!cd && cd !== lastSyncedEntityCd.value;
    if (force) lastSyncedEntityCd.value = cd;
    applyCompanyFromEntity(e, force);
    if (cd) {
      void loadEntityCompanyProfile(cd, force);
      void loadEntityOfficersAndOptions(cd);
      (docRef.value as any)?.syncDirectorModeFromEntity?.(cd);
    }
  },
  { immediate: true },
);

watch(
  () => props.chatFormData,
  (fd) => {
    if (!fd) return;
    const cd = String(fd.entityCd || fd.icpCd || '').trim();
    const name = String(fd.company_name || fd.entityName || '').trim();
    if (cd && cd !== lastSyncedEntityCd.value) {
      lastSyncedEntityCd.value = cd;
      if (name) values.company_name = name;
      void loadEntityCompanyProfile(cd, true);
      void loadEntityOfficersAndOptions(cd);
      (docRef.value as any)?.syncDirectorModeFromEntity?.(cd);
    } else if (name) {
      values.company_name = name;
    }
  },
  { deep: true },
);

watch(entityCd, (cd) => {
  if (!cd) return;
  const force = cd !== lastSyncedEntityCd.value;
  if (force) lastSyncedEntityCd.value = cd;
  void loadEntityCompanyProfile(cd, force);
  void loadEntityOfficersAndOptions(cd);
  (docRef.value as any)?.syncDirectorModeFromEntity?.(cd);
});

const overallProgress = computed(() => {
  const local = (docRef.value as any)?.gatherLocalState?.() || { rows: [1] };
  const rowIds: number[] = Array.isArray(local.rows) && local.rows.length ? local.rows : [1];
  // basis ref 노출값 — Third Schedule 전환 시 진행률 재계산
  const basisRaw = (docRef.value as any)?.basis;
  const basis =
    (basisRaw && typeof basisRaw === 'object' && 'value' in basisRaw ? basisRaw.value : basisRaw)
    || local.basis
    || 'constitution';
  const keys = progressKeysForRows(rowIds, basis);
  if (!keys.length) return 0;
  const filled = keys.filter((k) => values[k]).length;
  return Math.round((filled / keys.length) * 100);
});
watch(overallProgress, (v) => emit('progress-change', v), { immediate: true });
watch(
  () => {
    const b = (docRef.value as any)?.basis;
    return b && typeof b === 'object' && 'value' in b ? b.value : b;
  },
  () => emit('progress-change', overallProgress.value),
);

const editMode = computed(() => !!props.editTaskId);
const taskConfigSteps = ref<string[]>([]);
const creating = ref(false);
const progress = ref('');
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; });
watch(progress, (v) => { presetProgress.message = v; });

onMounted(async () => {
  prefetchB15Cache();
  const code = props.selection?.task?.TASK_CODE;
  if (code) {
    try {
      const cfg = await getTaskConfigByCode(code);
      taskConfigSteps.value = (cfg as any)?.processSteps || [];
    } catch { /* noop */ }
  }
});

const availableTemplates = computed(() => (props.selection?.documents || []).filter((d) => d.tmpId));

function classifyDoc(name: string): 'resolution' | 'letter' | 'section58' | 'other' {
  const n = String(name || '');
  // Section 58C 를 letter 보다 먼저 — 이름에 resign/letter 가 섞여도 58C 우선
  if (DOC_SHEET_PATTERNS.section58.test(n) || /58\s*c|cessation|notification/i.test(n)) return 'section58';
  if (DOC_SHEET_PATTERNS.resolution.test(n) || /dcr|circular/i.test(n)) return 'resolution';
  if (DOC_SHEET_PATTERNS.letter.test(n) || /resignation\s*letter|letter.*resign/i.test(n)) return 'letter';
  return 'other';
}

function gatherState() {
  const local = (docRef.value as any)?.gatherLocalState?.() || {};
  return { values: { ...values }, local };
}
function restoreState(s: any) {
  if (s?.values) Object.assign(values, s.values);
  if (s?.local) (docRef.value as any)?.restoreLocalState?.(s.local);
}

function parseSignersArr(raw: any): any[] {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return [];
}

async function handleCreateTask() {
  if (creating.value) return;
  if (!effectiveTitle.value) {
    progress.value = 'Please enter a Task Title.';
    return;
  }
  // 인라인/바 입력 중이면 먼저 커밋
  if (inputKey.value) commitInput();

  const local = (docRef.value as any)?.gatherLocalState?.() || { rows: [1] };
  const rows: number[] = Array.isArray(local.rows) && local.rows.length ? local.rows : [1];
  const boardSigners = parseSignersArr(local.signers);
  const signers58Map: Record<string, any[]> = local.signers58 || {};
  const letterSignersMap: Record<string, any[]> = local.letterSigners || {};

  const valuesSnap = { ...values };
  const hasCompany = !!String(valuesSnap.company_name || '').trim();
  const hasDirector = rows.some((id) => !!String(valuesSnap[`dir${id}_full_name`] || '').trim());
  if (!hasCompany || !hasDirector) {
    progress.value = '⚠ Please fill Company Name and at least one resigning director Full Name.';
    return;
  }

  // 문서별 싸이너가 비어 있으면 규칙대로 보강 (수동 편집본은 유지)
  if (!boardSigners.length || rows.some((id) => !parseSignersArr(signers58Map[id] || signers58Map[String(id)]).length)) {
    runAutoSigners('fill-empty');
  }
  const localAfter = (docRef.value as any)?.gatherLocalState?.() || local;
  const boardFinal = parseSignersArr(localAfter.signers);
  const signers58Final: Record<string, any[]> = localAfter.signers58 || signers58Map;
  const letterFinal: Record<string, any[]> = localAfter.letterSigners || letterSignersMap;

  const reasonsSnap: Record<number | string, string> = {
    ...(local.reasons || {}),
    ...(localAfter.reasons || {}),
  };
  // Reasons for cessation → values 에도 복사 (mapper collectRows 폴백 + DATA_JSON 확인용)
  for (const id of rows) {
    const reason = String(reasonsSnap[id] ?? reasonsSnap[String(id)] ?? '').trim();
    if (reason) {
      valuesSnap[`dir${id}_reason`] = reason;
      valuesSnap[`dir${id}_reason_for_cessation`] = reason;
    }
  }

  const baseInput = {
    values: valuesSnap,
    rows,
    reasons: reasonsSnap,
    nationality: localAfter.nationality || local.nationality || {},
    directorMode: (localAfter.directorMode || local.directorMode || 'sole') as 'sole' | 'multiple',
    basis: (localAfter.basis || local.basis || 'constitution') as 'constitution' | 'articles' | 'third_schedule',
    signers: boardFinal,
  };

  const docs = availableTemplates.value;
  if (!docs.length) {
    progress.value = '⚠ No templates linked to this preset — cannot generate documents.';
    return;
  }

  creating.value = true;
  progress.value = editMode.value ? 'Updating task…' : 'Creating task…';
  try {
    const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
    const taskFlow = {
      createDocument: steps.length === 0 ? true : steps.includes('doc'),
      requestTSign: steps.includes('t-sign'),
      proceedWithEFiling: steps.includes('e-filing'),
    };
    const directors = buildResignRegisterDirectors(valuesSnap, rows, reasonsSnap);
    const officerData = {
      entityCd: entityCd.value || null,
      presetForm: gatherState(),
      // Register Detail / L_OFFICER.CESSATION_DATE 갱신용
      directors,
    };

    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) {
        progress.value = 'Error: invalid edit task id';
        creating.value = false;
        return;
      }
      await fetch(`/api/startTask/tasks/${taskId}/data`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskData: { formData: officerData } }),
      });
      await fetch(`/api/startTask/tasks/${taskId}/title`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskTitle: effectiveTitle.value }),
      });
    } else {
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityCd: entityCd.value || null,
          taskTypeCd: props.selection.task.TASK_CODE,
          taskTypeName: props.selection.task.TASK_NAME,
          taskTitle: effectiveTitle.value,
          taskFlow,
          officerData,
        }),
      }).then((r) => r.json());
      taskId = taskRes?.data?.taskId || taskRes?.taskId;
      if (!taskId) {
        progress.value = `Error creating task: ${taskRes?.error?.message || taskRes?.message || 'no taskId'}`;
        creating.value = false;
        return;
      }
    }

    progress.value = 'Preparing documents…';

    type DocSave = {
      templateId: number;
      label: string;
      personName: string;
      entityCd?: string;
      formData: Record<string, any>;
      signers?: any[];
    };
    const docsToSave: DocSave[] = [];

    for (const d of docs) {
      const kind = classifyDoc(d.templateName || d.docCode || '');
      const baseLabel = String(d.templateName || 'Document').replace(/\.docx?$/i, '');

      if (kind === 'resolution' || kind === 'other') {
        // DCR — 사임 이사 제외한 board signers
        const formData = buildResignFormData({ ...baseInput, focusRowId: null, signers: boardFinal });
        docsToSave.push({
          templateId: d.tmpId!,
          label: baseLabel,
          personName: valuesSnap.company_name || 'Company',
          entityCd: entityCd.value || undefined,
          formData,
          signers: boardFinal.length ? boardFinal : undefined,
        });
        continue;
      }

      // Letter / Section 58C — director 마다 1부
      for (const id of rows) {
        const name = valuesSnap[`dir${id}_full_name`] || `Director ${id}`;
        let perSigners: any[] = [];
        if (kind === 'letter') {
          perSigners = parseSignersArr(letterFinal[id] || letterFinal[String(id)]);
          if (!perSigners.length && name) {
            perSigners = [toSignerEntry({ id: `resign-${id}`, name }, 'director', 'Director', `ltr-${id}`)];
          }
        } else if (kind === 'section58') {
          perSigners = parseSignersArr(signers58Final[id] || signers58Final[String(id)]);
          if (!perSigners.length) {
            perSigners = entitySecretaries.value.map((s) =>
              toSignerEntry(s, 'secretary', s.positionName || 'Company Secretary', `sec-${id}`),
            );
          }
        }
        const formData = buildResignFormData({
          ...baseInput,
          focusRowId: id,
          signersForDoc: perSigners,
        });
        docsToSave.push({
          templateId: d.tmpId!,
          label: rows.length > 1 ? `${baseLabel} — ${name}` : baseLabel,
          personName: name,
          entityCd: entityCd.value || undefined,
          formData,
          signers: perSigners.length ? perSigners : undefined,
        });
      }
    }

    if (docsToSave.length) {
      try {
        console.log('[Resignation] prepare-documents', {
          taskId,
          count: docsToSave.length,
          docs: docsToSave.map((x) => ({
            templateId: x.templateId,
            label: x.label,
            formKeys: Object.keys(x.formData || {}).slice(0, 40),
            hasSigners: Array.isArray(x.signers) && x.signers.length > 0,
            sample: {
              company_name: x.formData.company_name,
              dir_full_name: x.formData.dir_full_name,
              resign_director_full_name: x.formData.resign_director_full_name,
              constitution_clause_number: x.formData.constitution_clause_number,
              resolution_date: x.formData.resolution_date,
              reason: x.formData.reason,
              reason_resignation: x.formData.reason_resignation,
              reason_removal: x.formData.reason_removal,
              is_reason_resignation: x.formData.is_reason_resignation,
            },
          })),
        });
        const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }),
        }).then((r) => r.json());
        if (!saveRes?.success) {
          progress.value = `⚠ Documents: ${saveRes?.message || 'unknown'}`;
          console.warn('[Resignation] prepare-documents failed:', saveRes);
        }
      } catch (err: any) {
        console.warn('[Resignation] document save failed:', err);
        progress.value = `⚠ Document save failed: ${err?.message || err}`;
      }
    } else {
      progress.value = '⚠ No documents to save.';
    }

    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
    } catch { /* ignore */ }
    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }),
      });
    } catch { /* ignore */ }
    try {
      progress.value = 'Loading next screen…';
      const [taskDetailRes, docListRes] = await Promise.all([
        fetch(`/api/startTask/tasks/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
        fetch(`/api/startTask/doc-cust-save/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
      ]);
      sessionStorage.setItem(
        `presetTaskDetailPrefetch:${taskId}`,
        JSON.stringify({ taskDetail: taskDetailRes, docList: docListRes }),
      );
    } catch { /* ignore */ }

    emit('task-created', { taskId: taskId!, taskTitle: effectiveTitle.value });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (e: any) {
    console.error('[Resignation] create failed', e);
    progress.value = `Error: ${e?.message || e}`;
  } finally {
    creating.value = false;
  }
}

defineExpose({ gatherState, restoreState, createTask: handleCreateTask });
</script>

<template>
  <div class="rsn-panel" :class="{ 'rsn-studio': studio }">
    <div v-if="!studio" class="co-header">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME }}</div>
        <div class="co-subtitle">Resignation of Director — document package</div>
      </div>
      <v-btn variant="text" size="small" class="text-none" @click="emit('back')">
        <v-icon start size="16">mdi-arrow-left</v-icon>Back
      </v-btn>
    </div>

    <div v-if="!studio" class="co-title-row">
      <span class="ct-label">Task Title</span>
      <v-text-field
        v-model="titleModel"
        placeholder="e.g. Resignation of Director — Acme Sdn Bhd"
        variant="outlined"
        density="compact"
        hide-details
        class="ct-input"
      />
    </div>

    <div v-if="!studio" class="co-entity">
      <v-icon size="18" color="primary">mdi-domain</v-icon>
      <span>{{ entityData?.companyName || entityData?.entityName || 'Entity' }}</span>
      <v-btn variant="tonal" size="x-small" class="text-none ml-auto" @click="emit('change-entity')">Change</v-btn>
    </div>

    <div v-if="inputKey" class="co-inputbar">
      <div class="lbl">
        {{ labelize(inputKey) }}
        <span v-if="typeOf(inputKey) === 'date'" class="opt">(optional)</span>
      </div>
      <div class="row">
        <v-menu v-if="typeOf(inputKey) === 'date'" v-model="dateMenu" :close-on-content-click="false">
          <template #activator="{ props: mp }">
            <v-text-field
              v-bind="mp"
              v-model="inputText"
              placeholder="DD/MM/YYYY"
              variant="outlined"
              density="compact"
              hide-details
              append-inner-icon="mdi-calendar"
            />
          </template>
          <v-date-picker @update:model-value="onPickDate" />
        </v-menu>
        <v-text-field
          v-else
          v-model="inputText"
          :placeholder="labelize(inputKey)"
          variant="outlined"
          density="compact"
          hide-details
          autofocus
          @keydown.enter="commitInput"
        />
        <v-btn color="primary" size="small" variant="flat" @click="commitInput">Set</v-btn>
        <v-btn v-if="typeOf(inputKey) === 'date'" variant="text" size="small" @click="skipInput">Skip</v-btn>
      </div>
    </div>

    <div class="co-doc-body">
      <DirectorResignationDoc ref="docRef" :entity-cd="entityCd" />
    </div>

    <div v-if="!studio || progress" class="co-footer">
      <v-btn v-if="!studio" variant="outlined" class="text-none" @click="emit('back')">Back</v-btn>
      <div class="co-foot-right">
        <span v-if="progress" class="co-progress" :class="{ err: /error|⚠/i.test(progress) }">{{ progress }}</span>
        <v-btn
          v-if="!studio"
          color="primary"
          class="text-none"
          :loading="creating"
          :disabled="creating"
          :prepend-icon="editMode ? 'mdi-content-save' : 'mdi-check'"
          @click="handleCreateTask"
        >
          {{ editMode ? 'Update Task' : 'Create Task' }}
        </v-btn>
      </div>
    </div>

    <AddressInputModal
      v-model="addrModal"
      :label="addrLabel"
      :has-components="false"
      :initial="addrInitial"
      @apply="applyAddress"
    />
  </div>
</template>

<style scoped>
.rsn-panel { display:flex; flex-direction:column; }
.rsn-studio { flex:0 0 auto; width:min(1000px, 100%); background:transparent; }
.rsn-studio .co-doc-body { background:transparent; padding:0; }
.co-header { display:flex; align-items:center; justify-content:space-between; padding:12px 16px; border-bottom:1px solid #eef0f6; }
.co-title { font-weight:700; font-size:15px; }
.co-subtitle { font-size:12px; color:#8a94a6; }
.co-title-row { display:flex; align-items:center; gap:10px; padding:10px 14px; border-bottom:1px solid #eef0f6; }
.co-title-row .ct-label { font-size:12px; font-weight:700; color:#5b6472; white-space:nowrap; }
.co-title-row .ct-input { max-width:520px; }
.co-entity { display:flex; align-items:center; gap:8px; background:#f0f5ff; padding:8px 14px; font-size:13px; }
.co-inputbar { padding:10px 14px; background:#fafbfe; border-bottom:1px solid #eef0f6; }
.co-inputbar .lbl { font-size:12px; font-weight:700; margin-bottom:6px; }
.co-inputbar .opt { font-weight:500; color:#8a94a6; }
.co-inputbar .row { display:flex; gap:8px; align-items:center; }
.co-doc-body { padding:16px 8px 24px; }
.co-footer { display:flex; justify-content:space-between; align-items:center; padding:12px 16px; border-top:1px solid #eef0f6; }
.co-foot-right { display:flex; align-items:center; gap:12px; }
.co-progress { font-size:12px; color:#5b6472; max-width:420px; }
.co-progress.err { color:#d64545; font-weight:600; }
</style>

<style>
.cdd-tbl { width:100%; border-collapse:collapse; margin-top:-1px; }
.cdd-tbl td, .cdd-tbl th { border:1px solid #000; padding:5px 8px; vertical-align:top; text-align:left; font-weight:400; }
.cdd-lcell { width:42%; }
.cdd-cbx { display:inline-flex; align-items:center; margin-right:14px; white-space:nowrap; cursor:pointer; }
.cdd-cbx .box {
  display:inline-flex; align-items:center; justify-content:center;
  width:14px; height:14px; border:1px solid #333; margin-right:4px; vertical-align:-3px;
  font-size:11px; line-height:1;
}
</style>
